import { useNavigate } from "@tanstack/solid-router";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { For, Show, createMemo } from "solid-js";
import { HomeCityField } from "~/components/HomeCityField";
import { fetchMeetupMe, fetchMeetupNights, updateMeetupHomeCity } from "~/lib/api";
import { isMeetupFull, isMeetupPast, sortMeetupNights, type MeetupNight } from "~/lib/meetup";

export function MeetupListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const meQuery = createQuery(() => ({
    queryKey: ["meetup-me"],
    queryFn: fetchMeetupMe,
  }));
  const nightsQuery = createQuery(() => ({
    queryKey: ["meetup-nights"],
    queryFn: fetchMeetupNights,
  }));
  const homeCityMutation = createMutation(() => ({
    mutationFn: (city: string) => updateMeetupHomeCity(city),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["meetup-me"] }),
  }));

  const sorted = createMemo(() =>
    sortMeetupNights(nightsQuery.data?.data ?? [], meQuery.data?.data.home_city ?? ""),
  );

  function openNight(night: MeetupNight) {
    navigate({ to: "/meetup/$slug", params: { slug: night.slug } });
  }

  return (
    <section class="card">
      <p class="status-chip">Meetups</p>
      <h1>City nights</h1>
      <p class="muted">
        Free to register. Not a conference ticket or Connect Pass. You can still go to another city.
      </p>

      <HomeCityField
        value={meQuery.data?.data.home_city ?? ""}
        disabled={homeCityMutation.isPending || meQuery.isPending}
        hint="Sorts this list. Does not hide other cities."
        onChange={(city) => homeCityMutation.mutate(city)}
      />
      <Show when={homeCityMutation.isError}>
        <div class="error-box">{(homeCityMutation.error as Error).message}</div>
      </Show>

      <Show when={nightsQuery.isPending}>
        <p class="muted">Loading meetups…</p>
      </Show>
      <Show when={nightsQuery.isError}>
        <div class="error-box">{(nightsQuery.error as Error).message}</div>
      </Show>
      <Show when={nightsQuery.isSuccess && sorted().length === 0}>
        <p class="muted" style={{ "margin-top": "16px" }}>
          No nights listed yet. Check back soon.
        </p>
      </Show>
      <Show when={(meQuery.data?.data.history ?? []).length > 0}>
        <h2 class="meetup-history-heading">Your past nights</h2>
        <p class="muted">Matched from Meetup.com by the same email you signed in with.</p>
        <ul class="event-list meetup-history-list">
          <For each={meQuery.data?.data.history ?? []}>
            {(night) => (
              <li>
                <div class="event-pill meetup-history-pill">
                  <div>
                    <span class="city-pill">{night.city || "Meetup"}</span>
                    <strong>{night.title}</strong>
                    <span>
                      <Show when={night.starts_at}>
                        {new Date(night.starts_at!).toLocaleDateString("en-AU", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </Show>
                      <Show when={night.venue}>
                        {(venue) => (
                          <>
                            <Show when={night.starts_at}>{" · "}</Show>
                            {venue()}
                          </>
                        )}
                      </Show>
                    </span>
                    <Show when={(night.speakers ?? []).length > 0}>
                      <ul class="meetup-history-speakers">
                        <For each={night.speakers}>
                          {(speaker) => (
                            <li>
                              <strong>{speaker.name}</strong>
                              <Show when={speaker.talk}>
                                <span> — {speaker.talk}</span>
                              </Show>
                            </li>
                          )}
                        </For>
                      </ul>
                    </Show>
                  </div>
                </div>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show when={sorted().length > 0}>
        <ul class="event-list">
          <For each={sorted()}>
            {(night) => {
              const past = isMeetupPast(night);
              const full = isMeetupFull(night);
              const going = night.mine?.status === "going";
              const status = going ? "Going" : past ? "Started" : full ? "Full" : "Open";
              return (
                <li>
                  <button type="button" class="event-pill event-pill-btn" onClick={() => openNight(night)}>
                    <div>
                      <span class="city-pill">{night.city}</span>
                      <strong>{night.title}</strong>
                      <span>
                        {new Date(night.starts_at).toLocaleString("en-AU", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                          hour: "numeric",
                          minute: "2-digit",
                          timeZone: "UTC",
                        })}
                        {night.location ? ` · ${night.location}` : ""}
                      </span>
                    </div>
                    <span class="status-chip">{status}</span>
                  </button>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>
    </section>
  );
}
